// usePhilippinesV2Tab.ts
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../../services/api";
import { useChat, Message } from "../../../contexts/ChatContext";
import { useCategories } from "../../../contexts/CategoryContext";
import { useSubCategories } from "../../../contexts/SubCategoryContext";

// ─── Same pattern as useAstriaIndiaV2Tab.ts — a simple, linear flow:
//
//   Fill one screen → Submit → API call → receive response → show it
//
// Deliberately does NOT go through ChatContext's chats/currentChat list, for
// the exact reasons documented in useAstriaIndiaV2Tab.ts's header comment:
// each submission is a single, self-contained question-and-answer with no
// history and no other component needs to see it.
//
// Unlike India V2's tabs (labeled text in userMessage), this lane sends a
// typed `philippinesV2Wizard` req.body object — the contract documented in
// helper/astriaPhilippinesV2Service.js / philippinesIndonesiaV2Shared.js on
// the backend. userMessage is still required by the backend's generic
// `if (!userMessage) return 400` guard, but its content is never parsed for
// this lane, so a short static placeholder is sent.
//
// The backend response for this lane is never LLM-generated — it's one line
// picked deterministically from a fixed copy pack (see
// helper/philippinesIndonesiaV2Shared.js) — so there's no structured
// multi-field data to extract, only `phVnIdV2Data: {tab, lineIndex}`
// metadata riding alongside the plain-text content.

export interface PhilippinesV2Wizard {
  tab: string;
  // "en" | "ph" — selects which copy pack the backend picks the response
  // from (see helper/astriaPhilippinesV2Service.js's EN_COPY_PACK/PH_COPY_PACK).
  // Sourced from the screen's own EN/PH toggle (philippinesV2Language.ts),
  // not the account-wide preferredLanguage.
  lang?: string;
  mood?: string;
  intensity?: string;
  connection?: string;
  tone?: string;
  flow?: string;
  readiness?: string;
  emotion_zone?: string;
  location?: string;
  issue?: string;
  desired?: string;
  latest_message?: string;
  feeling?: string;
}

const CATEGORY_NAME = "Astria Philippines V2";

interface UsePhilippinesV2TabReturn {
  loading: boolean;
  error: string | null;
  message: Message | null;
  send: (subCategoryName: string, wizard: PhilippinesV2Wizard) => Promise<void>;
  reset: () => void;
  goToChat: () => Promise<void>;
}

export function usePhilippinesV2Tab(): UsePhilippinesV2TabReturn {
  const navigate = useNavigate();
  const { startNewChat, setCurrentChat } = useChat();
  const { categories } = useCategories();
  const { subCategories } = useSubCategories();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<Message | null>(null);

  const resolveIds = (subCategoryName: string) => {
    const category = categories.find((c) => c.name === CATEGORY_NAME);
    const categoryId = category?.id || null;
    const subCategory = categoryId
      ? subCategories.find(
          (sc) => sc.categoryId === categoryId && sc.name === subCategoryName,
        )
      : null;
    const subCategoryId = subCategory?.id || null;
    return { categoryId, subCategoryId };
  };

  const send = async (subCategoryName: string, wizard: PhilippinesV2Wizard) => {
    setLoading(true);
    setError(null);
    setMessage(null);

    const { categoryId, subCategoryId } = resolveIds(subCategoryName);

    if (!categoryId) {
      setError(
        `The "${CATEGORY_NAME}" category isn't set up yet. Please try again shortly.`,
      );
      setLoading(false);
      return;
    }
    if (!subCategoryId) {
      setError(
        `The "${subCategoryName}" subcategory isn't set up yet. Please try again shortly.`,
      );
      setLoading(false);
      return;
    }

    try {
      const userId = localStorage.getItem("userId");
      const token = localStorage.getItem("token");
      const baseUrl = api.defaults.baseURL || "";

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(
        `${baseUrl}/backend/chat/create?stream=true`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            userMessage: "check-in",
            categoryId,
            subCategoryId,
            userId: userId || undefined,
            philippinesV2Wizard: wizard,
          }),
        },
      );

      if (!response.ok || !response.body) {
        let errMsg = "The server couldn't be reached. Please try again.";
        try {
          const errData = await response.json();
          if (errData?.message) errMsg = errData.message;
        } catch {
          // ignore — keep default message
        }
        setError(errMsg);
        setLoading(false);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let content = "";
      let phVnIdV2Data: { tab: string; lineIndex: number } | undefined;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";

        for (const part of parts) {
          for (const line of part.split("\n")) {
            if (!line.startsWith("data:")) continue;
            const dataStr = line.replace(/^data:\s*/, "");
            if (!dataStr) continue;

            let payload: any;
            try {
              payload = JSON.parse(dataStr);
            } catch {
              continue;
            }

            if (payload?.text) {
              content += payload.text;
            }
            if (payload?.done && payload?.phVnIdV2Data) {
              phVnIdV2Data = payload.phVnIdV2Data;
            }
          }
        }
      }

      if (!content.trim()) {
        setError("We didn't receive a response back. Please try again.");
        setLoading(false);
        return;
      }

      setMessage({
        id: `ai_${Date.now()}`,
        content,
        sender: "ai",
        timestamp: new Date(),
        categoryId,
        subCategoryId,
        phVnIdV2Data,
      });
    } catch (err) {
      console.error(`Astria Philippines V2 (${subCategoryName}) error:`, err);
      setError("The server couldn't be reached. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setMessage(null);
    setError(null);
  };

  // "Continue in Chat" opens the app-wide "Companion Talk" category itself
  // (no subcategory) — same destination as Sanctuary's floating HealJai
  // Talk button and every other V2 lane — not a fresh chat on the
  // Philippines V2 subcategory the user just used.
  const goToChat = async () => {
    try {
      const category = categories.find((c) => c.name === "Companion Talk");
      const newChat = await startNewChat(category?.id || undefined);
      setCurrentChat(newChat);
      navigate(`/sanctuary?chat=${newChat.id}`);
    } catch (err) {
      console.error("Failed to start Companion Talk chat:", err);
      setError("Failed to start new chat. Please try again.");
    }
  };

  return { loading, error, message, send, reset, goToChat };
}
